frappe.provide("making_tax_digital.UrlToIpResolver");

making_tax_digital.UrlToIpResolver = class {
    /**
     * Utility class to resolve the public IP address of a hostname using
     * Google's DNS-over-HTTPS API (https://dns.google/resolve).
     * 
     * Why this class is needed:
     * -------------------------
     * Browsers cannot directly perform low-level DNS A-record lookups.
     * They rely on OS networking APIs, which are not exposed to JavaScript.
     * Therefore, to resolve a hostname to its public IP from browser JS,
     * we must use a third-party DNS API service such as Google DNS-over-HTTPS.
     * 
     * Features:
     * ---------
     * - Performs DNS "A" record lookup using Google DNS JSON API.
     * - Caches the IP for 15 minutes to avoid unnecessary API calls.
     * - Retries API call up to 3 times.
     * - Never throws errors — returns:
     *      • Fresh IP if resolved
     *      • Cached IP if API fails
     *      • null if nothing can be resolved
     * - All methods & variables are static, so the class behaves like a singleton.
     */

    static API_URL = "https://dns.google/resolve";
    static CACHE_TTL = 15 * 60 * 1000; // 15 minutes in ms
    static FETCH_TIMEOUT_MS = 5000;

    // class-level cache, keyed by normalized hostname
    // { [hostname]: { ip: string, timestamp: number } }
    static _cache = {};

    // --------------------------------------------------------------

    static async resolvePublicIP(hostname) {
        /**
         * Returns the resolved public IP from cache or fresh lookup.
         */
        const normalizedHostname = this._normalizeHostname(hostname);
        if (!normalizedHostname) {
            return null;
        }

        const cachedEntry = this._cache[normalizedHostname];
        if (cachedEntry && cachedEntry.ip && cachedEntry.timestamp) {
            const age = Date.now() - cachedEntry.timestamp;
            if (age < this.CACHE_TTL) {
                return cachedEntry.ip;
            }
        }

        const freshIp = await this._fetchWithRetry(normalizedHostname);

        if (freshIp) {
            this._cache[normalizedHostname] = {
                ip: freshIp,
                timestamp: Date.now()
            };
            return freshIp;
        }

        // If API failed → return stale cache if available
        return cachedEntry ? cachedEntry.ip : null;
    }

    // --------------------------------------------------------------

    static async _fetchWithRetry(hostname) {
        /**
         * Try DNS lookup 3 times.
         * Never throws.
         */
        for (let attempt = 1; attempt <= 3; attempt++) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.FETCH_TIMEOUT_MS);

            try {
                const url = `${this.API_URL}?name=${encodeURIComponent(hostname)}&type=A`;
                const resp = await fetch(url, {
                    method: "GET",
                    signal: controller.signal
                });

                if (!resp.ok) continue;

                const data = await resp.json();

                if (data.Answer && data.Answer.length > 0) {
                    const firstARecord = data.Answer.find(a => a.type === 1);
                    if (firstARecord) {
                        return firstARecord.data;
                    }
                }
            } catch (e) {
                // Ignore errors silently
            } finally {
                clearTimeout(timeoutId);
            }
        }

        return null;
    }

    static _normalizeHostname(hostname) {
        if (!hostname || typeof hostname !== "string") {
            return "";
        }

        return hostname.trim().toLowerCase();
    }
}
